// @ts-strict-ignore
import { Component, DestroyRef, inject, model, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DataObject } from '@app/_shared/interfaces';
import { OrderSearchResult } from '@app/_shared/interfaces/http/order/order';
import { MiddleEllipsisPipe } from '@app/_shared/pipes/middle-ellipsis.pipe';
import { StoreNameByIdPipe } from '@app/_shared/pipes/store-name-by-id.pipe';
import { DashboardService } from '@app/dashboard/dashboard.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { translateSignal } from '@jsverse/transloco';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Overlay } from 'primeng/overlay';
import { Tag } from 'primeng/tag';
import { catchError, debounceTime, filter, finalize, fromEvent, map, of, take } from 'rxjs';

@Component({
  selector: 'sb-search-bar',
  templateUrl: './search-bar.component.html',
  host: {
    class: 'flex w-full relative',
  },
  imports: [
    Overlay,
    Tag,
    InputIcon,
    IconField,
    InputTextModule,
    FormsModule,
    FontAwesomeModule,
    StoreNameByIdPipe,
    MiddleEllipsisPipe,
  ],
})
export class SearchBarComponent implements OnInit {
  private router = inject(Router);
  private _dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);

  readonly searchResultSelected = output<void>();

  public activeStoreId: string | number = null;

  public searchControl = model<string>('');
  public searchResults = signal<DataObject<OrderSearchResult>[]>([]);
  public searchOpen = signal<boolean>(false);
  public searching = signal<boolean>(false);
  public searchNoResults = signal<boolean>(false);

  readonly noResultsFoundLabel = translateSignal('components.searchBar.noResultsFound');
  readonly placeholderLabel = translateSignal('components.searchBar.placeholder');

  private prevSearchControlValue = signal<string>('');

  search() {
    this.prevSearchControlValue.set(this.searchControl());
    if (this.searchControl() === null || this.searchControl().trim() === '') {
      return;
    }
    this.searching.set(true);
    return this._dashboardService
      .searchOrderByWebNo('search', this.searchControl())
      .pipe(
        take(1),
        map(({ data }) => {
          this.searchResults.set(data);
          this.searchNoResults.set(data.length === 0);
          this.searchOpen.set(true);
        }),
        catchError(() => {
          return of([]);
        }),
        finalize(() => {
          this.searching.set(false);
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    fromEvent(document, 'keyup')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: KeyboardEvent) => event && event.key && !['enter', 'escape'].includes(event.key.toLowerCase())),
        debounceTime(300),
        map((event) => ({
          key: event.key.toLowerCase(),
          term: this.searchControl(),
        })),
        filter(({ term }) => typeof term === 'string' && term.trim() !== this.prevSearchControlValue()),
        map(() => {
          this.search();
        }),
      )
      .subscribe();
  }

  clearSearch() {
    this.searchControl.set('');
    this.resetSearch();
  }

  resetSearch() {
    this.searchResults.set([]);
    this.searchNoResults.set(false);
    this.searchOpen.set(false);
  }

  onOptionSelected(orderId: number) {
    this.resetSearch();
    this.searchResultSelected.emit();
    this.router.navigate(['/', 'orders', orderId]);
  }
}
